//
// Software Name: Orange Design System Charts
// SPDX-FileCopyrightText: Copyright (c) 2023 - 2026 Orange SA
// SPDX-License-Identifier: MIT
//
// This software is distributed under the MIT license.
//
import { cloneDeepObject } from '../../tools/clone-deep-object';
import { ODSChartsMode } from '../ods-chart-mode';
export const DEFAULT_ARROW_SIZE = 0; // in px. Set to 0 to disable arrow
export class ODSChartsPopoverManager {
    constructor() {
        this.dispose = undefined;
        this.show = undefined;
    }
}
/**
 * ODSChartsPopoverItem defines an item to be displayed in the popover/tooltip
 * It is used in {@link ODSChartsPopoverDefinition} methods to customize the content of the popover/tooltip
 * Example of use:
 * ```
 * themeManager.externalizePopover(undefined, {
    ...ODSCharts.ODSChartsPopoverManagers.NONE,
    getPopupContentLine: ({ seriesName, itemValue, seriesIndex }) => {
      return `<p>${themeManager.getPopoverMarker(seriesIndex)}This is my HTML code of one line for ${itemValue} of ${seriesName}</p>`;
    },
  });
 * ```
 */
export class ODSChartsPopoverItem {
}
/**
 * {@link ODSChartsPopoverDefinition} defines the interface of the manager of externalized
 * popover or tooltip {@link ODSChartsTheme.externalizePopover}.
 *
 * {@link ODSChartsPopoverDefinition} is not used directly. Instead, we use the variable **{@link ODSChartsPopoverDefinition}.
 * {@link ODSChartsPopoverManagers} gives 3 default {@link ODSChartsPopoverDefinition}
 * managers for Apache ECharts, Boosted 5 and Boosted 4.
 *
 * You probably need to use one of those.
 *
 * But even if you use one of those, you may specialize it with specific(s)
 * implementation(s) with one of the {@link ODSChartsPopoverDefinition} method(s).
 *
 * For example, you may specify the content of the tooltip:
 * ```
 *                 themeManager.externalizePopover(undefined, {
 *                  ...ODSCharts.ODSChartsPopoverManagers.NONE,
 *                  getPopupContent: (tooltipElements) => {
 *                    return 'This is my HTML code that may content <a href="https://github.com/Orange-OpenSource/ods-charts">a link</a>';
 *                  },
 *                });
 * ```
 * or if you want to use Boosted 5 tooltip renderer:
 * ```
 *                 themeManager.externalizePopover(undefined, {
 *                  ...ODSCharts.ODSChartsPopoverManagers.BOOSTED5,
 *                  getPopupContent: (tooltipElements) => {
 *                    return 'This is my HTML code that may content <a href="https://github.com/Orange-OpenSource/ods-charts">a link</a>';
 *                  },
 *                });
 * ```
 */
export class ODSChartsPopoverDefinition {
}
export class ODSChartsPopoverDefinitionWithRenderer extends ODSChartsPopoverDefinition {
    testIfMouseIsOverTooltip(previousPopover) {
        var _a;
        try {
            // We test if the mouse is over the previous tooltip to cancel the creation
            // of a new one and allow the mouse to enter over this previous one
            const mousePosition = { top: window.event.clientY, left: window.event.clientX };
            const tooltipRect = (_a = previousPopover.tip) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (tooltipRect &&
                mousePosition.top > tooltipRect.top - this.tooltipMarging &&
                mousePosition.top < tooltipRect.top + tooltipRect.height + this.tooltipMarging &&
                mousePosition.left > tooltipRect.left - this.tooltipMarging &&
                mousePosition.left < tooltipRect.left + tooltipRect.width + this.tooltipMarging) {
                return true;
            }
        }
        catch (error) { }
        return false;
    }
}
export var ODSChartsPopoverAxisPointer;
(function (ODSChartsPopoverAxisPointer) {
    ODSChartsPopoverAxisPointer["none"] = "none";
    ODSChartsPopoverAxisPointer["shadow"] = "shadow";
    ODSChartsPopoverAxisPointer["line"] = "line";
    ODSChartsPopoverAxisPointer["cross"] = "cross";
})(ODSChartsPopoverAxisPointer || (ODSChartsPopoverAxisPointer = {}));
export var ODSChartsPopoverTooltipTrigger;
(function (ODSChartsPopoverTooltipTrigger) {
    ODSChartsPopoverTooltipTrigger["xAxis"] = "xAxis";
    ODSChartsPopoverTooltipTrigger["yAxis"] = "yAxis";
    ODSChartsPopoverTooltipTrigger["grid"] = "grid";
})(ODSChartsPopoverTooltipTrigger || (ODSChartsPopoverTooltipTrigger = {}));
/**
 * Configuration of the externalizePopover feature {@link ODSChartsTheme.externalizePopover}.
 *
 */
export class ODSChartsPopoverConfig {
}
class BOOSTED5_Definition extends ODSChartsPopoverDefinitionWithRenderer {
    constructor() {
        super(...arguments);
        this.testAvailability = () => {
            let availability = true;
            try {
                if (undefined === boosted) {
                    availability = false;
                }
            }
            catch (error) {
                availability = false;
            }
            if (!availability) {
                console.warn('BOOSTED 5 popover/tooltip rendering is not avalable: boosted variable is not accessible!');
            }
            return availability;
        };
        this.getOrCreatePopupInstance = this._getOrCreatePopupInstance.bind(this);
        this.tooltipDelay = 0;
        this.tooltipMarging = 15;
    }
    _getOrCreatePopupInstance(selector, title, htmlContent, enterable, mode) {
        var _a;
        try {
            let previousPopover = boosted.Popover.getInstance(document.querySelector(selector));
            if (previousPopover) {
                if (enterable && this.testIfMouseIsOverTooltip(previousPopover)) {
                    return undefined;
                }
                previousPopover.dispose();
            }
        }
        catch (error) { }
        const allowList = cloneDeepObject(boosted.Tooltip.Default.allowList);
        allowList.span = ['style', 'class'];
        allowList.div = ['class'];
        allowList.label = ['class'];
        let container = document.getElementById('ods-chart-popover-container-' + mode);
        if (!container) {
            container = document.createElement('div');
            if ([ODSChartsMode.DARK, ODSChartsMode.LIGHT].includes(mode)) {
                container.setAttribute('data-bs-theme', mode);
            }
            container.id = 'ods-chart-popover-container-' + mode;
            container.classList.add('ods-charts-context');
            (_a = document.querySelector('body')) === null || _a === void 0 ? void 0 : _a.append(container);
        }
        return boosted.Popover.getOrCreateInstance(document.querySelector(selector), {
            allowList: allowList,
            html: true,
            trigger: 'click',
            placement: 'top',
            container: container,
            title: title,
            content: htmlContent,
            customClass: enterable ? '' : 'pe-none',
        });
    }
}
class BOOSTED4_Definition extends ODSChartsPopoverDefinitionWithRenderer {
    constructor() {
        super(...arguments);
        this.testAvailability = () => {
            let availability = true;
            try {
                if (undefined === boosted) {
                    availability = false;
                }
            }
            catch (error) {
                availability = false;
            }
            if (!availability) {
                console.warn('BOOSTED 4 popover/tooltip rendering is not avalable: boosted variable is not accessible!');
            }
            return availability;
        };
        this.getOrCreatePopupInstance = this._getOrCreatePopupInstance.bind(this);
        this.tooltipDelay = 0;
        this.tooltipMarging = 10;
    }
    _getOrCreatePopupInstance(selector, title, htmlContent, enterable, mode) {
        var _a;
        const elt = document.querySelector(selector);
        const whiteList = cloneDeepObject(boosted.Tooltip.Default.whiteList);
        whiteList.span = ['style', 'class'];
        whiteList.div = ['class'];
        whiteList.label = ['class'];
        if (elt && elt.chartPopover) {
            try {
                if (enterable && this.testIfMouseIsOverTooltip(elt.chartPopover)) {
                    return undefined;
                }
                if (elt.chartPopover.config.title === title && elt.chartPopover.config.content === htmlContent) {
                    elt.chartPopover.update();
                    return undefined;
                }
                elt.chartPopover.dispose();
            }
            catch (error) { }
        }
        let container = document.getElementById('ods-chart-popover-container-' + mode);
        if (!container) {
            container = document.createElement('div');
            if ([ODSChartsMode.DARK, ODSChartsMode.LIGHT].includes(mode)) {
                container.setAttribute('data-bs-theme', mode);
            }
            container.id = 'ods-chart-popover-container-' + mode;
            container.classList.add('ods-charts-context');
            (_a = document.querySelector('body')) === null || _a === void 0 ? void 0 : _a.append(container);
        }
        const popover = new boosted.Popover(elt, {
            whiteList: whiteList,
            html: true,
            trigger: 'click',
            placement: 'top',
            container: container,
            title: title,
            content: htmlContent,
            customClass: '',
        });
        elt.chartPopover = popover;
        if (!enterable) {
            window.setTimeout(() => {
                try {
                    elt.chartPopover.tip.style.pointerEvents = 'none';
                }
                catch (error) { }
            });
        }
        return popover;
    }
}
/**
 * `ODSChartsPopoverManagers` is used to specify popoverDefinition parameter (popup/tooltip renderer) of {@link ODSChartsTheme.externalizePopover}.
 *
 * Available popover/tooltip renderer:
 * - `ODSChartsPopoverManagers.NONE`: to use default ECharts template to externalize tooltip/popover HTML element, implementing Orange Design System
 * - `ODSChartsPopoverManagers.BOOSTED5`: to use Boosted 5 tooltip/popover
 * - `ODSChartsPopoverManagers.BOOSTED4`: to use Boosted 4 tooltip/popover
 *
 * **WARNING**: Boosted 4 or Boosted 5 rendering requires dependency on the boosted library and access to the boosted global variable.
 */
export const ODSChartsPopoverManagers = {
    BOOSTED5: new BOOSTED5_Definition(),
    BOOSTED4: new BOOSTED4_Definition(),
    NONE: {},
};
